import { Box, Button, Heading, Select } from 'grommet';
import React from 'react';
import { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import stringify from 'json-stringify-pretty-compact';
import AceEditor from 'react-ace';
import { useLocalStorage } from 'react-use';
import { RootAction } from '../store/actions';
import { addLayer, removeLayer } from '../store/actions/layers';
import BoardEditor from './BoardEditor';
import { BoardObjectType } from '../@types';
import { Color } from '../constants/Color';
import { Shape } from '../constants/Shape';

import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';

enum Theme {
  GITHUB = 'github',
  MONOKAI = 'monokai',
}

const AddBoardObjectsForm: React.FunctionComponent = () => {
  const [boardFileString, setBoardFileString] = useLocalStorage(
    'board-editor',
    `[
  {
    "color": "black",
    "shape": "star",
    "id": "6",
    "x": 1,
    "y": 6
  }
]
`,
  );
  const [theme, setTheme] = useLocalStorage('theme', Theme.GITHUB);
  const dispatch: Dispatch<RootAction> = useDispatch();

  const parseBoardFileString = () => {
    const parsedValues: BoardObjectType[] = JSON.parse(boardFileString);
    return parsedValues.map((parsedValue) => ({
      ...parsedValue,
      // Color and shape need to be uppercase in case they're lowercase.
      color: parsedValue.color.toUpperCase() as Color,
      shape: parsedValue.shape.toUpperCase() as Shape,
    }));
  };

  return (
    <Box elevation="large" align="center" pad="medium" fill>
      <Heading level="2">Board Editor</Heading>
      <Box fill gap="small" align="center" justify="stretch">
        <Button
          label="Use Interactive Board Editor"
          onClick={() => {
            try {
              dispatch(
                addLayer(
                  'Board Editor',
                  <BoardEditor
                    onLoad={(boardObjects) => {
                      setBoardFileString(stringify(boardObjects, { maxLength: 20 }));
                      dispatch(removeLayer('board-editor'));
                    }}
                    key={boardFileString}
                    boardObjects={parseBoardFileString()}
                  />,
                  [
                    {
                      key: 'discard',
                      label: 'Discard',
                      action: addLayer(
                        'Unsaved board',
                        'Are you sure you want to discard',
                        [
                          {
                            key: 'yes',
                            label: 'Yes',
                            action: [removeLayer('board-editor'), removeLayer('confirm-discard')],
                          },
                          { key: 'no', label: 'No', action: removeLayer('confirm-discard') },
                        ],
                        'confirm-discard',
                      ),
                    },
                  ],
                  'board-editor',
                  false,
                  false,
                ),
              );
            } catch (e) {
              if (e instanceof Error) {
                dispatch(
                  addLayer('Error parsing string', e.message, [
                    {
                      label: 'Close',
                      action: (id) => removeLayer(id),
                    },
                  ]),
                );
              }
            }
          }}
        />
        <Box fill="horizontal" align="start">
          <Select
            options={Object.values(Theme)}
            value={theme}
            onChange={({ option }) => setTheme(option)}
          />
        </Box>
        <AceEditor
          mode="json"
          theme={theme}
          onChange={(value) => setBoardFileString(value)}
          value={boardFileString}
          width="100%"
          height="100%"
        />
      </Box>
    </Box>
  );
};
export default AddBoardObjectsForm;
